import type { InputHTMLAttributes, TextareaHTMLAttributes, ReactNode } from "react";
import { useId } from "react";
import "./Field.css";

interface BaseProps {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
}

export function TextField({
  label,
  hint,
  error,
  required,
  className = "",
  onClear,
  ...rest
}: BaseProps &
  InputHTMLAttributes<HTMLInputElement> & {
    /** When set, renders a circular ✕ button inside the input (right-aligned)
     *  that calls this on click — shown only while the field has a value. */
    onClear?: () => void;
  }) {
  const id = useId();
  const showClear =
    !!onClear && !rest.disabled && rest.value != null && String(rest.value) !== "";
  const input = (
    <input
      id={id}
      className={`field__input ${showClear ? "field__input--clearable" : ""}`}
      aria-invalid={error ? true : undefined}
      aria-describedby={hint || error ? `${id}-desc` : undefined}
      required={required}
      {...rest}
    />
  );
  return (
    <div className={`field ${error ? "field--error" : ""} ${className}`}>
      <label className="field__label" htmlFor={id}>
        {label}
        {required ? <span className="field__req" aria-hidden="true"> *</span> : null}
      </label>
      {onClear ? (
        <div className="field__input-wrap">
          {input}
          {showClear ? (
            <button
              type="button"
              className="field__clear"
              onClick={onClear}
              aria-label={`Clear ${label}`}
              title="Clear"
            >
              <span aria-hidden="true">✕</span>
            </button>
          ) : null}
        </div>
      ) : (
        input
      )}
      <FieldDesc id={`${id}-desc`} hint={hint} error={error} />
    </div>
  );
}

export function TextAreaField({
  label,
  hint,
  error,
  required,
  className = "",
  ...rest
}: BaseProps & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const id = useId();
  return (
    <div className={`field ${error ? "field--error" : ""} ${className}`}>
      <label className="field__label" htmlFor={id}>
        {label}
        {required ? <span className="field__req" aria-hidden="true"> *</span> : null}
      </label>
      <textarea
        id={id}
        className="field__input field__textarea"
        aria-invalid={error ? true : undefined}
        aria-describedby={hint || error ? `${id}-desc` : undefined}
        required={required}
        {...rest}
      />
      <FieldDesc id={`${id}-desc`} hint={hint} error={error} />
    </div>
  );
}

export function SelectField({
  label,
  labelHidden,
  hint,
  error,
  required,
  children,
  className = "",
  ...rest
}: BaseProps &
  InputHTMLAttributes<HTMLSelectElement> & {
    children: ReactNode;
    /** Drop the visible label (the select still gets it as an aria-label) —
     *  for selects whose options make them self-explanatory. */
    labelHidden?: boolean;
  }) {
  const id = useId();
  return (
    <div className={`field ${error ? "field--error" : ""} ${className}`}>
      {labelHidden ? null : (
        <label className="field__label" htmlFor={id}>
          {label}
          {required ? <span className="field__req" aria-hidden="true"> *</span> : null}
        </label>
      )}
      <select
        id={id}
        className="field__input field__select"
        aria-label={labelHidden ? label : undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={hint || error ? `${id}-desc` : undefined}
        required={required}
        {...rest}
      >
        {children}
      </select>
      <FieldDesc id={`${id}-desc`} hint={hint} error={error} />
    </div>
  );
}

function FieldDesc({ id, hint, error }: { id: string; hint?: string; error?: string }) {
  if (!hint && !error) return null;
  return (
    <p id={id} className={`field__desc ${error ? "field__desc--error" : ""}`}>
      {error || hint}
    </p>
  );
}
